import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error.message);
      localStorage.setItem('adminToken', json.data.token);
      navigate('/admin/overview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 font-['Plus_Jakarta_Sans']"
      style={{ background: 'radial-gradient(circle at top right, #1e3c72, #00255a 60%, #001a42 100%)' }}
    >
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src="/images/logo_w_nobg.png" alt="IASC Consultancy" className="h-10 w-10 object-contain rounded-lg bg-white" />
            <h1 className="text-2xl text-white tracking-tight font-semibold"><a href="/">IASC Consultancy</a></h1>
          </div>
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
            <span className="text-xs text-[#ff8483] uppercase tracking-widest flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">security</span>
              Admin Access Only
            </span>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-[0px_20px_60px_rgba(0,0,0,0.3)] p-10">
          <header className="mb-8">
            <h2 className="text-3xl font-bold text-[#00255a] mb-2">Secure Login</h2>
            <p className="text-[#44474f]">Enter your administrative credentials to manage global operations.</p>
          </header>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#00255a]/80 block px-1">Email Address</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#44474f] text-xl">
                  alternate_email
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@iasc.com"
                  className="w-full bg-[#f2f4f6] border border-[#c4c6d1] rounded-lg py-3.5 pl-11 pr-4 focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center px-1">
                <label className="text-xs font-semibold text-[#00255a]/80 block">Password</label>
                <a className="text-xs text-[#365da3] hover:text-[#1e3c72]" href="#forgot-access">Forgot Access?</a>
              </div>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#44474f] text-xl">
                  lock_person
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full bg-[#f2f4f6] border border-[#c4c6d1] rounded-lg py-3.5 pl-11 pr-11 focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#44474f]"
                >
                  <span className="material-symbols-outlined text-xl">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
            </div>

            <div className="flex items-center py-2">
              <input type="checkbox" className="w-4 h-4 rounded border-[#747781] text-[#00255a]" />
              <label className="ml-3 text-[#44474f]">Remember this workstation for 12 hours</label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-4 rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              <span>{loading ? 'Verifying Identity...' : 'Authenticate Access'}</span>
              {!loading && <span className="material-symbols-outlined">arrow_forward</span>}
            </button>
          </form>

          <footer className="mt-8 pt-6 border-t border-[#c4c6d1]/30 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-[#747781] text-xs">
                <span className="material-symbols-outlined text-sm">verified_user</span>
                256-bit End-to-End Encryption
              </div>
              <p className="text-[11px] leading-tight text-[#747781] uppercase tracking-tighter max-w-[280px] mx-auto">
                Unauthorized access attempts are logged and reported to the system administrator.
              </p>
            </div>
          </footer>
        </div>

        <div className="mt-8 flex justify-between px-2">
          <a className="text-white/60 hover:text-white flex items-center gap-2" href="/">
            <span className="material-symbols-outlined text-sm">public</span>
            Public Site
          </a>
          <a className="text-white/60 hover:text-white flex items-center gap-2" href="/contact">
            <span className="material-symbols-outlined text-sm">support_agent</span>
            IT Support
          </a>
        </div>
      </div>
    </div>
  );
}